import { IonFooter, IonModal, useIonRouter } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { CoreValueCard } from "@/components/CoreValue/CoreValueCard";
import { Button } from "@/components/Global/Button";
import { Toolbar } from "@/components/Global/Toolbar";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";

export function ModalCoreValuesPersonalizeCompleted() {
	const { t } = useTranslation();
	const posthog = usePostHog();
	const router = useIonRouter();
	const modalRef = useRef<HTMLIonModalElement>(null);
	const modal = useSelectorModalStore.use.coreValuesPersonalizeCompleted();
	const present = useSelectorModalStore.use.present();
	const dismiss = useSelectorModalStore.use.dismiss();

	const queryCoreValues = useQuery({ ...queryOptions_coreValuesChosen(), enabled: modal.isOpen });

	const thisCoreValue =
		modal.id && !queryCoreValues.isFetching
			? queryCoreValues.data?.find((coreValue) => coreValue.id === modal.id)
			: null;

	const nextCoreValue = queryCoreValues.data?.find((coreValue) => !coreValue.customName);

	return (
		<IonModal
			ref={modalRef}
			isOpen={modal.isOpen}
			onWillPresent={() => {
				present("coreValuesPersonalizeCompleted");
				posthog.capture("Modal CoreValues Personalize Completed Present");
			}}
			onWillDismiss={() => {
				dismiss("coreValuesPersonalizeCompleted", { id: null });
				posthog.capture("Modal CoreValues Personalize Completed Dismiss");
			}}
			initialBreakpoint={1}
			breakpoints={[0, 1]}
			className="ion-h-auto ion-rounded-4xl"
		>
			<div className="ion-padding mt-4 -rotate-2 pt-12">
				{!!thisCoreValue && <CoreValueCard coreValue={thisCoreValue} />}
			</div>

			<IonFooter className="ion-no-border">
				<Toolbar className="ion-bg-brown-200 ion-p-4">
					<div className="flex flex-col gap-2">
						<Button
							data-attr="modal.core-values.personalize.completed.confirm"
							expand="block"
							variant="primary"
							onClick={() => {
								modalRef.current?.dismiss();

								if (nextCoreValue) {
									router.push(
										Routes.CoreValuesChat({ id: nextCoreValue?.id }),
										"forward",
										"replace",
									);
								} else {
									router.push(Routes.CoreValuesDashboard(), "back", "replace");
								}
							}}
						>
							{nextCoreValue
								? t("modal.core-values.personalize.completed.confirm")
								: t("modal.core-values.personalize.completed.finish")}
						</Button>
						<Button
							data-attr="modal.core-values.personalize.completed.cancel"
							expand="block"
							variant="secondary"
							onClick={() => {
								modalRef.current?.dismiss();
								router.push(Routes.CoreValuesDashboard(), "back");
							}}
						>
							{t("modal.core-values.personalize.completed.cancel")}
						</Button>
					</div>
				</Toolbar>
			</IonFooter>
		</IonModal>
	);
}
